import { z } from 'zod';
import { IdSchema } from './common.js';

/**
 * The people a campaign will call (docs/36 D1b, 0123): lists with fields of their own, filled from
 * a spreadsheet or one lead at a time through a public endpoint.
 *
 * The rules that decide whether a row is a usable lead live here, not in the API or the portal,
 * because both need them and must agree: the browser previews a CSV with them before anything is
 * sent, and the API applies them again to what arrives. A row the preview called good and the
 * server refused would be the worst kind of import.
 */

export const DIALLER_FIELD_TYPES = ['text', 'number', 'date', 'yes_no', 'email'] as const;
export type DiallerFieldType = (typeof DIALLER_FIELD_TYPES)[number];

export const DIALLER_FIELD_KEY = /^[a-z][a-z0-9_]{0,39}$/;

/** A header or a label as a field key: "Renewal Date" is renewal_date. */
export function fieldKey(label: string): string {
  const k = label.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return /^[a-z]/.test(k) ? k : `f_${k}`.slice(0, 40);
}

export const DiallerFieldSchema = z.object({
  id: IdSchema,
  key: z.string(),
  label: z.string(),
  type: z.enum(DIALLER_FIELD_TYPES),
  seq: z.number().int(),
});
export type DiallerField = z.infer<typeof DiallerFieldSchema>;

export const DiallerListSchema = z.object({
  id: IdSchema,
  name: z.string(),
  description: z.string().nullable(),
  fields: z.array(DiallerFieldSchema),
  records: z.number().int(),
  suppressed: z.number().int(),
  created_at: z.string(),
});
export type DiallerList = z.infer<typeof DiallerListSchema>;

export const DiallerListUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  fields: z.array(z.object({
    key: z.string().regex(DIALLER_FIELD_KEY, 'Lower case letters, digits and underscores, starting with a letter.'),
    label: z.string().trim().min(1).max(80),
    type: z.enum(DIALLER_FIELD_TYPES).default('text'),
  })).max(60).default([]),
}).strict().superRefine((l, issue) => {
  const seen = new Set<string>();
  for (const [i, f] of l.fields.entries()) {
    // The built-in columns are not custom fields; a field called "phone" would be ambiguous.
    if (RESERVED.has(f.key)) {
      issue.addIssue({ code: z.ZodIssueCode.custom, path: ['fields', i, 'key'], message: `${f.key} is already a column.` });
    }
    if (seen.has(f.key)) {
      issue.addIssue({ code: z.ZodIssueCode.custom, path: ['fields', i, 'key'], message: 'Used twice.' });
    }
    seen.add(f.key);
  }
});

const RESERVED = new Set(['phone', 'first_name', 'last_name', 'email', 'name']);

// =================================================================================== one person

/**
 * A number as international digits with no '+', or null if it cannot be one.
 *
 * 0821234567 on a South African account is 27821234567; +44 20 7946 0958 is 442079460958. A
 * number that is too short or too long to be a phone number anywhere (E.164: at most 15 digits,
 * and nothing real is under 8) is refused rather than stored and dialled wrongly.
 */
export function leadPhone(raw: string | null | undefined, homePrefix: string): string | null {
  if (!raw) return null;
  let d = String(raw).trim().replace(/[^\d+]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  else if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = homePrefix + d.slice(1);
  return /^[1-9][0-9]{7,14}$/.test(d) ? d : null;
}

/**
 * A value as its field's type says, or an error to say why not.
 *
 * Stored as JSON of the right type -- a number as a number, a date as YYYY-MM-DD, yes/no as a
 * boolean -- so a campaign can filter "renewal before March" without guessing at spreadsheet
 * formats every time it reads the list.
 */
export function fieldValue(type: DiallerFieldType, raw: unknown):
  { ok: true; value: string | number | boolean | null } | { ok: false; error: string } {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  const s = String(raw).trim();
  if (s === '') return { ok: true, value: null };
  switch (type) {
    case 'text':
      return s.length <= 1000 ? { ok: true, value: s } : { ok: false, error: 'longer than 1000 characters' };
    case 'number': {
      // "1 250,50" and "1,250.50" are both a thousand two hundred and fifty.
      const n = Number(s.replace(/\s/g, '').replace(/,(?=\d{3}(\D|$))/g, '').replace(',', '.'));
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: `"${s.slice(0, 20)}" is not a number` };
    }
    case 'yes_no': {
      const v = s.toLowerCase();
      if (['yes', 'y', 'true', '1', 'ja'].includes(v)) return { ok: true, value: true };
      if (['no', 'n', 'false', '0', 'nee'].includes(v)) return { ok: true, value: false };
      return { ok: false, error: `"${s.slice(0, 20)}" is not yes or no` };
    }
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && s.length <= 254
        ? { ok: true, value: s.toLowerCase() } : { ok: false, error: `"${s.slice(0, 30)}" is not an email address` };
    case 'date': {
      // ISO, or day first as South Africa writes it: 2026-03-01, 01/03/2026, 1-3-2026.
      let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
      let y: number, mo: number, d: number;
      if (m) { y = +m[1]!; mo = +m[2]!; d = +m[3]!; } else {
        m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
        if (!m) return { ok: false, error: `"${s.slice(0, 20)}" is not a date` };
        d = +m[1]!; mo = +m[2]!; y = +m[3]!;
      }
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
        return { ok: false, error: `"${s.slice(0, 20)}" is not a real date` };
      }
      return { ok: true, value: dt.toISOString().slice(0, 10) };
    }
  }
}

export const DiallerRowSchema = z.object({
  phone: z.string().max(40),
  first_name: z.string().max(120).nullable().optional(),
  last_name: z.string().max(120).nullable().optional(),
  email: z.string().max(254).nullable().optional(),
  fields: z.record(z.string().max(1000).nullable()).default({}),
});
export type DiallerRow = z.infer<typeof DiallerRowSchema>;

/** A spreadsheet, a slice at a time: 500 rows a request, so one bad file cannot hold the API. */
export const DiallerImportSchema = z.object({
  import_id: IdSchema.optional(),
  file_name: z.string().max(200).optional(),
  rows: z.array(DiallerRowSchema).min(1).max(500),
}).strict();

export interface DiallerImportResult {
  import_id: string;
  added: number;
  updated: number;
  suppressed: number;
  /** The row's index in this slice, and why. At most 50, so a wrong mapping does not return 500 errors. */
  rejected: Array<{ row: number; reason: string }>;
  rejected_total: number;
}

export interface DiallerRecord {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  fields: Record<string, string | number | boolean | null>;
  source: 'csv' | 'api' | 'manual';
  state: 'new' | 'suppressed' | 'dialled' | 'final';
  consent_at: string | null;
  consent_page: string | null;
  created_at: string;
}

// =================================================================================== trickle

/**
 * One lead, as whatever sent it wrote it.
 *
 * A web form, Zapier, Make and a CRM each name the same things differently -- "phone", "Phone
 * Number", "mobile", "cell"; "name" or "first_name" -- so the common spellings are all read, and
 * anything else is matched to the list's own fields by key or by label. What matches nothing is
 * named back in the reply, so an integrator sees at once that a field did not land.
 */
const PHONE_KEYS = ['phone', 'phone_number', 'mobile', 'mobile_number', 'cell', 'cellphone', 'tel', 'telephone', 'contact_number', 'number'];
const FIRST_KEYS = ['first_name', 'firstname', 'given_name', 'name_first'];
const LAST_KEYS = ['last_name', 'lastname', 'surname', 'family_name', 'name_last'];
const NAME_KEYS = ['name', 'full_name', 'fullname'];
const EMAIL_KEYS = ['email', 'email_address', 'e_mail'];
/** What a sender adds that is ours to read, never a field: the consent record's page, address and browser. */
const TRANSPORT_KEYS = ['page', 'page_url', 'source_url', 'consent_ip', 'user_agent'];

export function leadFromBody(
  body: Record<string, unknown>, fields: ReadonlyArray<Pick<DiallerField, 'key' | 'label'>>,
): { row: DiallerRow; ignored: string[]; page: string | null } {
  const byKey = new Map<string, unknown>();
  for (const [k, v] of Object.entries(body)) byKey.set(fieldKey(k), v);
  const first = (keys: readonly string[]): string | null => {
    for (const k of keys) {
      const v = byKey.get(k);
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return null;
  };

  let firstName = first(FIRST_KEYS);
  let lastName = first(LAST_KEYS);
  const full = first(NAME_KEYS);
  if (!firstName && !lastName && full) {
    // "Thandi van der Merwe": the first word is the first name and the rest is the surname,
    // which is right more often than any cleverer guess.
    const [f, ...rest] = full.split(/\s+/);
    firstName = f ?? null;
    lastName = rest.length ? rest.join(' ') : null;
  }

  const used = new Set([...PHONE_KEYS, ...FIRST_KEYS, ...LAST_KEYS, ...NAME_KEYS, ...EMAIL_KEYS, ...TRANSPORT_KEYS]);
  const out: Record<string, string | null> = {};
  const ignored: string[] = [];
  for (const [original, v] of Object.entries(body)) {
    const k = fieldKey(original);
    if (used.has(k)) continue;
    const field = fields.find((f) => f.key === k || fieldKey(f.label) === k);
    if (!field) { ignored.push(original); continue; }
    out[field.key] = v === null || v === undefined ? null : String(v).slice(0, 1000);
  }

  return {
    row: {
      phone: first(PHONE_KEYS) ?? '',
      first_name: firstName, last_name: lastName, email: first(EMAIL_KEYS), fields: out,
    },
    ignored,
    page: first(['page', 'page_url', 'source_url']),
  };
}

/**
 * A caller outside the platform that may add leads to one list (docs/38, 0124). It signs in with
 * SCRAM-SHA-256 for a short-lived token; the secret is shown once, when the client is made, and
 * the platform keeps only what SCRAM needs to check it.
 */
export const ApiClientSchema = z.object({
  id: IdSchema,
  name: z.string(),
  scope: z.literal('leads:write'),
  list_id: IdSchema.nullable(),
  secret_hint: z.string(),
  enabled: z.boolean(),
  uses: z.number().int(),
  last_used_at: z.string().nullable(),
  created_at: z.string(),
});
export type ApiClient = z.infer<typeof ApiClientSchema>;

export const ApiClientCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  list_id: IdSchema,
}).strict();

export const ApiClientUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  enabled: z.boolean().optional(),
}).strict();
